import { useEffect, useState } from "react";
import { useTranslation } from "../localization";
import { toast } from "../toast";

/** How long the check mark stands in for the copy icon after a copy. */
const COPIED_DURATION = 1500;

/**
 * The one behaviour every copy control shares: write the value to the clipboard, confirm it with a
 * toast, and report a short-lived "copied" state the control swaps its icon on.
 *
 * @param value - What lands in the clipboard; nothing is copied while it is null.
 * @returns Whether a copy just happened, and the function that performs one.
 */
export function useCopy(value: string | null | undefined) {
	/** Context */
	const { t } = useTranslation();

	/** State */
	const [isCopied, setIsCopied] = useState(false);

	/** Effects */
	useEffect(() => {
		if (!isCopied) return;
		const timeout = setTimeout(() => setIsCopied(false), COPIED_DURATION);
		return () => clearTimeout(timeout);
	}, [isCopied]);

	/** Functions */
	async function copy() {
		if (!value) return;
		await navigator.clipboard.writeText(value);
		setIsCopied(true);
		toast.info(t("general.copied_to_clipboard"));
	}

	/** Return */
	return { isCopied, copy };
}
